import { useEffect, useRef, useState } from "react";
import { Scissors, Play, Pause } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SLICE = 10;
const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

// Pick which 10s of a longer clip plays. onConfirm(start, posterDataUrl).
export default function VideoTrimModal({ file, duration, initialStart = 0, onConfirm, onClose }) {
  const vRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [start, setStart] = useState(initialStart);
  const [playing, setPlaying] = useState(false);
  const maxStart = Math.max(0, (duration || SLICE) - SLICE);

  useEffect(() => {
    if (!file) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    setStart(initialStart);
    return () => URL.revokeObjectURL(u);
  }, [file, initialStart]);

  const seek = (t) => { setStart(t); const v = vRef.current; if (v) { v.pause(); setPlaying(false); v.currentTime = t; } };
  const togglePreview = () => {
    const v = vRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); return; }
    v.currentTime = start; v.play().catch(() => {}); setPlaying(true);
  };
  const onTime = () => {
    const v = vRef.current;
    if (v && playing && v.currentTime >= start + SLICE) { v.pause(); v.currentTime = start; setPlaying(false); }
  };
  const confirm = () => {
    const v = vRef.current;
    let poster = null;
    try {
      if (v && v.videoWidth) {
        v.pause(); v.currentTime = start;
        const sc = Math.min(1, 1600 / Math.max(v.videoWidth, v.videoHeight));
        const c = document.createElement("canvas");
        c.width = Math.round(v.videoWidth * sc); c.height = Math.round(v.videoHeight * sc);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        poster = c.toDataURL("image/jpeg", 0.85);
      }
    } catch { poster = null; }
    onConfirm(start, poster);
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f16] border-2 border-[#5d8a52] text-white max-w-lg" data-testid="video-trim-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#f0c975] text-lg font-extrabold"><Scissors size={18} /> Pick your 10 seconds</DialogTitle>
          <DialogDescription className="text-white/70">This clip is {fmt(duration || 0)} long — slide to choose which 10 seconds play.</DialogDescription>
        </DialogHeader>
        {url && (
          <video ref={vRef} src={url} muted={false} playsInline preload="auto" onTimeUpdate={onTime}
            onLoadedMetadata={() => { if (vRef.current) vRef.current.currentTime = start; }}
            className="w-full rounded-xl bg-black aspect-video object-contain" data-testid="video-trim-player" />
        )}
        <div className="mt-1">
          <input type="range" min={0} max={maxStart} step={0.1} value={start} onChange={(e) => seek(parseFloat(e.target.value))}
            className="w-full accent-[#f0c975] cursor-pointer" data-testid="video-trim-slider" />
          <p className="text-center text-sm font-extrabold text-white mt-1" data-testid="video-trim-range">
            Plays {fmt(start)} → {fmt(Math.min(duration || SLICE, start + SLICE))}
          </p>
        </div>
        <div className="flex gap-2 mt-2">
          <button type="button" onClick={togglePreview} data-testid="video-trim-preview"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-[#5d8a52] py-2.5 font-extrabold hover:bg-white/10 transition-colors">
            {playing ? <Pause size={16} /> : <Play size={16} />} {playing ? "Stop" : "Preview 10s"}
          </button>
          <button type="button" onClick={confirm} data-testid="video-trim-confirm"
            className="flex-1 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] py-2.5 font-extrabold transition-colors">Use this slice</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
